import React, { useState, useEffect } from "react";
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Login.css";

const Login = ({ redirectToHome }) => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState(null);
  const [otpExpiryTime, setOtpExpiryTime] = useState(null);
  const [timer, setTimer] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // New state for loading spinner

  useEffect(() => {
    if (otpExpiryTime) {
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const timeLeft = otpExpiryTime - now;
        if (timeLeft <= 0) {
          clearInterval(interval);
          setTimer("OTP has expired. Please resend.");
          setGeneratedOtp(null);
        } else {
          const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
          setTimer(`OTP expires in ${minutes}:${seconds < 10 ? "0" : ""}${seconds}`);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [otpExpiryTime]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!otpVerified) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [otpVerified]);

  const handleSendOtp = (e) => {
    e.preventDefault();
    setIsLoading(true); // Start loading spinner

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/email/${email.trim()}`)
      .then(response => {
        if (response.data) {
          if (response.data.active != 1) {
            toast.error('User is not active. Please contact support.');
            setIsLoading(false); // Stop loading spinner
            return;
          }
          const generated = Math.floor(100000 + Math.random() * 900000);
          setGeneratedOtp(generated);
          const expiryTime = new Date().getTime() + 7 * 60 * 1000;
          setOtpExpiryTime(expiryTime);

          const emailPayload = {
            to: email.trim(),
            subject: "Your OTP for Sona R&D Portal Login",
            htmlContent: `
              <html>
                <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
                  <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 10px; background-color: #f9f9f9;">
                    <h2 style="color: #2A4D69; border-bottom: 2px solid #2A4D69; padding-bottom: 10px; margin-top: 0;">Sona R&D Portal</h2>
                    <p>Dear User,</p>
                    <p>You requested a One-Time Password (OTP) to log into the Sona R&D Portal.</p>
                    <div style="font-size: 1.5rem; font-weight: bold; background-color: #eef3f7; padding: 15px; border-radius: 5px; text-align: center; color: #2A4D69; margin: 20px 0; letter-spacing: 2px;">
                      ${generated}
                    </div>
                    <p>This OTP is valid for 7 minutes. Please do not share this OTP with anyone.</p>
                    <hr style="border: 0; border-top: 1px solid #eee; margin: 20px 0;" />
                    <p style="font-size: 0.8rem; color: #777;">This is an automated message. Please do not reply to this email.</p>
                  </div>
                </body>
              </html>
            `
          };

          axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/email/send`, emailPayload)
          .then(() => {
            toast.success("OTP has been sent to your email.");
            setOtpSent(true);
            setIsLoading(false); // Stop loading spinner
          })
          .catch((error) => {
            console.error("Error sending OTP email via backend:", error);
            toast.error("Failed to send OTP email. Please try again.");
            setIsLoading(false); // Stop loading spinner
          });
            
        }
      }) 
      .catch(error => {
        if (error.response) {
          console.error("Error response:", error.response);
          toast.error("Email not found. Please register first.");
        } else if (error.request) {
          console.error("No response received:", error.request);
          toast.error("The server is not responding. Please try again later.");
        } else {
          console.error("Error in setting up request:", error.message);
          toast.error("An unexpected error occurred. Please try again.");
        }
        setIsLoading(false); // Stop loading spinner
      });
  };

  const handleResendOtp = () => {
    setOtpSent(false);
    setGeneratedOtp(null);
    setTimer('00:00');
    handleSendOtp({ preventDefault: () => {} });
  };

  const handleVerifyOtp = () => {
    if (otp === String(generatedOtp)) {
      if (!email.trim()) {
        toast.error("Invalid user.");
        return;
      }

      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/email/${email.trim()}`)
        .then(response => {
          const user = response.data;
          sessionStorage.setItem("userEmail", email);
          sessionStorage.setItem("email", user.email);
          sessionStorage.setItem("access_level", user.accessLevel);
          sessionStorage.setItem("username", user.username);
          sessionStorage.setItem("designation", user.designation);
          sessionStorage.setItem("photo", response.data.photo);
          sessionStorage.setItem("userId", user.userId);

          toast.success("OTP verified successfully!");
          setOtpVerified(true);

          setTimeout(() => {
            window.location.href = "/home";
          }, 1000);
        })
        .catch(error => {
          toast.error("Invalid user. Please try again.");
          console.log(error);
        });

    } else {
      toast.error("Invalid OTP. Please try again.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (generatedOtp) {
      handleVerifyOtp();
    } else {
      handleSendOtp(e);
    }
  };

  return (
    <div className="container d-flex align-items-center justify-content-center min-vh-100 fixed-top">
      <ToastContainer />
      <div className="row justify-content-center w-100">
        <div className="col-10 col-md-8 col-lg-5">
          <div className="card shadow-sm p-4 fade-in border-light rounded-4 shadow">
            <h3 className="text-center mb-3 text-dark fw-semibold" style={{ fontSize: '1.5rem' }}>
              Welcome Back
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label fw-medium">Email Address</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{
                      outline: 'black',
                      boxShadow: 'none',
                      borderColor: '#DFDDDE'
                    }}
                  />
                </div>
              </div>
              {generatedOtp && (
                <div className="mb-3">
                  <label className="form-label fw-medium">Enter OTP</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fas fa-key"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Enter the OTP"
                      maxLength="6"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                      style={{
                        outline: 'black',
                        boxShadow: 'none',
                        borderColor: '#DFDDDE'
                      }}
                    />
                  </div>
                  <small className="d-block text-end mt-1 p-2" style={{ color: "#D32F2F", fontWeight: "550" }}>
                    {timer}
                  </small>
                </div>
              )}
              <div className="d-grid gap-2">
                {!generatedOtp && !otpSent && (
                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        Sending OTP
                        <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                      </>
                    ) : (
                      "Send OTP"
                    )}
                  </button>
                )}
                {otpSent && !generatedOtp && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm rounded-3 shadow-sm mt-2"
                    onClick={handleResendOtp}
                  >
                    Resend OTP
                  </button>
                )}
                {generatedOtp && (
                  <div className="d-flex justify-content-between">
                    <button
                      type="submit"
                      className="btn bg-primary btn-sm rounded-3 shadow-sm mt-2 text-white"
                    >
                      Verify OTP
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;